import React, { useRef, useState } from 'react';
import { Upload, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { assetUrl, uploadFile } from '../apis/ride-24';

const MAX_PHOTOS = 3;
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_BYTES = 5 * 1024 * 1024;

/**
 * Up to 3-photo uploader (presigned S3 upload via Taurian ride-24 API).
 * Props:
 *  - photos: [{ path, content_type, size }]
 *  - onChange: (newArray) => void
 */
const PhotoUploader = ({ photos = [], onChange }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handlePick = () => {
    if (photos.length >= MAX_PHOTOS) {
      toast.error(`Maximum ${MAX_PHOTOS} photos per ride`);
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, or WebP images are allowed');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('Image must be 5 MB or smaller');
      return;
    }

    setUploading(true);
    try {
      const uploaded = await uploadFile(file, { isPrivate: false });
      onChange([
        ...photos,
        {
          path: uploaded.path,
          content_type: uploaded.content_type,
          size: uploaded.size,
        },
      ]);
      toast.success('Photo uploaded');
    } catch (err) {
      console.error('Upload failed', err);
      toast.error(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          'Upload failed'
      );
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = (idx) => {
    onChange(photos.filter((_, i) => i !== idx));
  };

  return (
    <div data-testid="photo-uploader">
      <div className="grid grid-cols-3 gap-3">
        {Array.from({ length: MAX_PHOTOS }).map((_, idx) => {
          const photo = photos[idx];
          if (photo) {
            return (
              <div
                key={idx}
                className="relative aspect-square rounded-md overflow-hidden border border-gray-700 bg-gray-950 group"
                data-testid={`photo-slot-${idx}`}
              >
                <img
                  src={assetUrl(photo.path)}
                  alt={`Ride photo ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => handleRemove(idx)}
                  className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/80 text-white hover:bg-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Remove photo"
                  data-testid={`remove-photo-${idx}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            );
          }
          if (idx === photos.length) {
            return (
              <button
                key={idx}
                type="button"
                onClick={handlePick}
                disabled={uploading}
                className="aspect-square rounded-md border-2 border-dashed border-gray-700 hover:border-red-600 bg-gray-950 hover:bg-gray-900 text-gray-400 hover:text-red-400 flex flex-col items-center justify-center transition-colors disabled:opacity-50"
                data-testid="add-photo-button"
              >
                {uploading ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <>
                    <Upload className="w-6 h-6 mb-1" />
                    <span className="text-xs">Add photo</span>
                  </>
                )}
              </button>
            );
          }
          return (
            <div
              key={idx}
              className="aspect-square rounded-md border-2 border-dashed border-gray-800 bg-gray-950/50"
            />
          );
        })}
      </div>
      <p className="text-xs text-gray-500 mt-2">
        Up to {MAX_PHOTOS} photos. JPG, PNG, or WebP, max 5 MB each.
      </p>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileChange}
        className="hidden"
        data-testid="photo-file-input"
      />
    </div>
  );
};

export default PhotoUploader;
